import { config } from "@nova/configuration/browser"
import { ButtonPrimary, ButtonTertiary } from "@nova/component-btn"
import { selectLiveVideoFlightById } from "features/entity-caching/flights/selectors"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { initializeCenterModalForComponentAction } from "features/content-overlay/slice"
import {
    selectActiveSidePanelModal,
    setActiveSidePanelModal,
} from "panels/content/side-panel/slice"
import * as Icons from "@nova/component-icons"
import Image from "next/image"
import { ModalNames } from "features/modals/types"
import { useCallback, useRef, useState } from "react"
import { ClickAwayListener } from "@nova/component-click-away-listener"
import classNames from "classnames"
import { animationClass } from "features/data-management/workspace/constants"
import { ScrollableArea } from "@nova/component-scrollable-area"
import { useEffectUnsafe } from "@nova/use-effect-unsafe"
import { useUsageExceeded } from "features/data-management/workspace/subscriptions/hooks/useUsageExceeded"
import { usePaywalls } from "features/entity-caching/licenses/useLicensePaywall"
import { updateLiveVideoFlight } from "features/pubsub/entityActions/flights/liveVideo"
import { useDebounceDispatch } from "tools/store"
import { selectIsDomainWhiteListed } from "user/profile/selectors"
import { NovaSpinner } from "@nova/component-nova-spinner"
import { hideGlobalModal, showGlobalModal } from "runlevel/actions"
import { ToggleSwitch } from "@nova/component-toggle-switch"

const getLiveControllerAppUrl = () => {
    const environmentName = config.get("environmentName")
    if (environmentName === "production") return `https://live.mapnova.com`

    return `https://${environmentName}.live.mapnova.com`
}

export const LiveVideoTutorialModal = () => {
    const dispatch = useAppDispatch()
    const id = useAppSelector(selectActiveSidePanelModal())?.id
    const _flight = useAppSelector(selectLiveVideoFlightById(id || ""))
    const flightRef = useRef(_flight)
    const [, setRerender] = useState(new Date())
    const { isExceeded } = useUsageExceeded()
    const isWhitelisted = useAppSelector(selectIsDomainWhiteListed)

    // const userId = useAppSelector(selectUserID)
    // const authorFullName = useFullnameForUserId(_flight?.createdBy?.userId)?.fullName
    // const authorLicenses = useLicensesForAsigneeId(_flight?.createdBy?.userId)
    // const authorliteLicense = useMemo(
    //     () =>
    //         authorLicenses?.licenses.find((license) => license.subscriptionType === Licenses.Lite),
    //     [authorLicenses?.licenses]
    // )
    // const authorHasAddOn = useMemo(
    //     () => authorLicenses?.addOns.find((addOn) => addOn.subscriptionType === AddOns.LiveVideo),
    //     [authorLicenses]
    // )

    // @deprecated Not doing live video trials anymore. Didn't want to fully delete since
    // it's a lot of code to bring back.
    // const [timeLeftInTrial, setTimeLeftInTrial] = useState<string | undefined>(
    //     !!authorliteLicense?.liveVideoTrialExpiration && !authorHasAddOn
    //         ? formatHoursMinutesSecondsUntilDate(
    //               new Date(authorliteLicense?.liveVideoTrialExpiration)
    //           )
    //         : undefined
    // )
    // const [trialIsExpired, setTrialIsExpired] = useState(
    //     !!authorliteLicense?.liveVideoTrialExpiration && !authorHasAddOn && !isWhitelisted
    //         ? Math.round(
    //               (new Date(authorliteLicense?.liveVideoTrialExpiration).getTime() - Date.now()) /
    //                   1000
    //           ) <= 0
    //         : false
    // )

    // // Handles the live video trial clock.
    // useEffect(() => {
    //     const liveVideoTrialExpiration = authorliteLicense?.liveVideoTrialExpiration
    //     if (!authorliteLicense || !liveVideoTrialExpiration || authorHasAddOn || isWhitelisted)
    //         return

    //     const date = new Date(liveVideoTrialExpiration)

    //     const interval = setInterval(() => {
    //         const rawSecondsLeft = Math.round((date.getTime() - Date.now()) / 1000)
    //         if (rawSecondsLeft <= 0) {
    //             setTrialIsExpired(true)
    //             return clearInterval(interval)
    //         }

    //         setTimeLeftInTrial(formatHoursMinutesSecondsUntilDate(date))
    //     }, 1000)

    //     return () => {
    //         clearInterval(interval)
    //     }
    // }, [authorliteLicense, authorHasAddOn, isWhitelisted])

    const { debounceDispatch } = useDebounceDispatch(500)
    const cleanup = useCallback(() => {
        dispatch(setActiveSidePanelModal(undefined))
        dispatch(hideGlobalModal())
    }, [dispatch])

    // jank way to avoid the component getting destroyed by the server-flightId getting activated and the .fulfilled overwriting the state on the next render cycle
    useEffectUnsafe(() => {
        if (_flight) {
            flightRef.current = _flight
            setRerender(new Date())
        }
    }, [_flight])

    const flight = flightRef.current
    const [tutorialIsOpen, setTutorialIsOpen] = useState(false)
    if (!flight || (!("loading" in flight) && !("code" in flight)) || flight?.streamCreated) {
        return null
    }

    const restrictAutoSave = flight.saveRecording && isExceeded && !isWhitelisted

    return (
        <div
            className="fixed top-0 left-0 h-full w-full bg-[#0c0c0fc8] flex items-center justify-center z-[10000]"
            onKeyDown={(event) => {
                if (event.key === "Escape") {
                    cleanup()
                }
            }}
        >
            <ClickAwayListener className="flex h-full items-center" onClickAway={cleanup}>
                {/* {trialIsExpired ? (
                    <LiveVideoDataModalTrialExpired authorUserId={flight.createdBy?.userId} />
                ) : ( */}
                <div className="w-[480px] bg-black-800 rounded-xl text-white border border-gray-700 shadow-elevation-5">
                    <div className="px-8 pb-8 pt-6">
                        {/* Title */}
                        <div className="flex items-center pb-2 border-b border-gray-700">
                            <h3 className="flex-grow select-none">Connect a Drone</h3>
                            <Icons.SmallIcon
                                Icon={Icons.XClose}
                                onClick={cleanup}
                                className="text-gray-100 hover:text-gray-0 cursor-pointer"
                            />
                        </div>

                        {/* {timeLeftInTrial && (
                                <div className="flex justify-between items-center pb-4 mt-4 border-b border-gray-700 select-none">
                                    <small className="xs text-lazeryellow">
                                        {`Time left in ${userId === flight.createdBy?.userId || !authorFullName ? "Your" : authorFullName + `'${authorFullName.at(-1)?.toLowerCase() === "s" ? "" : "s"}`} 24-hour trial`}
                                    </small>
                                    <h5 className="mono text-gray-0">{timeLeftInTrial}</h5>
                                </div>
                            )} */}

                        <div className="text-center mt-8 flex flex-col gap-2">
                            <Image
                                src="/images/logos/image.webp"
                                alt="dji-logo"
                                width={39}
                                height={23}
                                className="mx-auto select-none"
                            />
                            <div className="xs text-gray-300 select-none">
                                Enter into DJI controller:
                            </div>
                            <strong>{getLiveControllerAppUrl()}</strong>
                            <div className="xs text-gray-300 mt-6 select-none">Use the code:</div>
                            {flight.code ? (
                                <div
                                    className="text-4xl font-normal leading-[120%]"
                                    data-tid="live-video-code"
                                >
                                    {flight.code}
                                </div>
                            ) : (
                                <NovaSpinner width={24} height={24} className="self-center my-3" />
                            )}
                        </div>
                        <h4 className="text-gray-0 text-center mt-5 select-none">
                            For best results, enter Camera View in the Pilot 2 App and switch to
                            your main camera before connecting.
                        </h4>
                        {!tutorialIsOpen && (
                            <div className="flex flex-col gap-4 mt-6">
                                <div className="xs text-gray-300 flex-1 my-auto">More options</div>
                                <div className="flex items-center">
                                    <h5 className="w-[145px] text-gray-100">Stream Name</h5>
                                    <input
                                        className="rounded-lg h-[40px] py-2 pl-4 pr-2 text-gray-300 bg-gray-700 !outline-none focus:outline-none border-transparent focus:border-transparent focus:ring-0"
                                        type="text"
                                        defaultValue={flight.name}
                                        autoFocus
                                        onChange={(e) => {
                                            if (e.target.value)
                                                debounceDispatch(
                                                    updateLiveVideoFlight({
                                                        id: flight.id,
                                                        updates: { name: e.target.value },
                                                    })
                                                )
                                        }}
                                    />
                                </div>
                                <div className="flex items-center">
                                    <h5 className="w-[145px] text-gray-100">Auto Save Video</h5>
                                    <ToggleSwitch
                                        isToggled={flight.saveRecording && !restrictAutoSave}
                                        disabled={restrictAutoSave}
                                        setIsToggled={(e) =>
                                            dispatch(
                                                updateLiveVideoFlight({
                                                    id: flight.id,
                                                    updates: { saveRecording: e },
                                                })
                                            )
                                        }
                                        isError={restrictAutoSave}
                                    />
                                </div>
                                {restrictAutoSave && <RestrictAutoSave />}
                            </div>
                        )}
                        <div className="xs text-gray-300 mt-6">Need help?</div>
                        <div
                            className="mt-2 flex items-center cursor-pointer gap-2"
                            onClick={() => setTutorialIsOpen((e) => !e)}
                        >
                            <Icons.Icon
                                Icon={Icons.ArrowDropdownRight}
                                className={classNames(animationClass, "cursor-pointer", {
                                    "rotate-90": tutorialIsOpen,
                                    "text-white-400 group-hover:text-gray-0": !tutorialIsOpen,
                                })}
                            />
                            <h4 className="text-gray-100">Tutorial</h4>
                        </div>
                        <ScrollableArea
                            className={classNames(
                                {
                                    "h-auto max-h-[300px]": tutorialIsOpen,
                                    "h-0": !tutorialIsOpen,
                                },
                                "mt-4 flex flex-col gap-2"
                            )}
                        >
                            <div>1. On your DJI Controller, open the DJI Pilot2 app.</div>
                            <Image
                                src={"/images/live-video-tutorial/1.png"}
                                width={416}
                                height={233}
                                alt="step-1"
                            />
                            <div className="mt-4">2. Tap on the Open Platforms button.</div>
                            <Image
                                src={"/images/live-video-tutorial/2.png"}
                                width={416}
                                height={233}
                                alt="step-2"
                            />
                            <div className="mt-4">
                                3. Type in the link:&nbsp;
                                {getLiveControllerAppUrl()}
                            </div>
                            <Image
                                src={"/images/live-video-tutorial/3.png"}
                                width={416}
                                height={235}
                                alt="step-3"
                            />
                            <div className="mt-4">
                                4. Enter the Access Code:&nbsp;
                                {flight.code ? flight.code : "{######}"}
                            </div>
                            <Image
                                src={"/images/live-video-tutorial/4.png"}
                                width={416}
                                height={235}
                                alt="step-4"
                            />
                        </ScrollableArea>
                    </div>
                    <div className="flex px-4 py-3">
                        <div className="grow" />
                        <ButtonTertiary title="Close" onClick={cleanup} className="w-[2rem]" />
                    </div>
                </div>
                {/* )} */}
            </ClickAwayListener>
        </div>
    )
}
export const LiveVideoDataModal = () => {
    const dispatch = useAppDispatch()
    useEffectUnsafe(() => {
        dispatch(initializeCenterModalForComponentAction(ModalNames.LiveVideoTutorialModal))
        dispatch(showGlobalModal())
        return () => {
            dispatch(hideGlobalModal())
        }
    }, [])

    return null
}

const RestrictAutoSave = () => {
    const { showLicensePaywallModal } = usePaywalls()
    return (
        <div>
            <div className="flex gap-2">
                <Icons.SmallIcon Icon={Icons.Error} className="text-orange-500" />
                <h5>Auto Save is Unavailable</h5>
            </div>
            <div className="flex flex-col my-2">
                <small>You have exceeded your data usage limit.</small>
                <small className="mb-2">
                    Upgrade your license or free up space to enable auto-saving.
                </small>
            </div>
            <ButtonPrimary
                title={"Upgrade License"}
                RightIcon={Icons.ArrowRight}
                onClick={() => showLicensePaywallModal("usage")}
            />
        </div>
    )
}
